'use client';

import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import * as React from 'react';
import { DateRange } from 'react-day-picker';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import { ptBR } from 'date-fns/locale';

export enum DateFilterType {
    EMAIL_SENT_AT = 'EMAIL_SENT_AT',
    CONCILIATION_DATE = 'CONCILIATION_DATE',
}

export const DATE_TYPE_OPTIONS = [
    { value: DateFilterType.EMAIL_SENT_AT, label: 'Data de envio do e-mail' },
    { value: DateFilterType.CONCILIATION_DATE, label: 'Data da conciliação' },
];

interface DateRangePickerProps extends React.HTMLAttributes<HTMLDivElement> {
    onDateChange: (startDate: string | undefined, endDate: string | undefined, dateType?: DateFilterType) => void;
    initialDateRange?: DateRange;
    initialDateType?: DateFilterType;
    placeholder?: string;
    showDateTypeSelector?: boolean;
}

export interface DateRangePickerRef {
    clear: () => void;
}

export const DateRangePicker = React.forwardRef<DateRangePickerRef, DateRangePickerProps>(({
    className,
    onDateChange,
    initialDateRange,
    initialDateType = DateFilterType.EMAIL_SENT_AT,
    placeholder = 'Selecione um intervalo de datas',
    showDateTypeSelector = false,
}, ref) => {
    const [date, setDate] = React.useState<DateRange | undefined>(initialDateRange);
    const [dateType, setDateType] = React.useState<DateFilterType>(initialDateType);
    const [open, setOpen] = React.useState(false);

    const handleDateSelect = React.useCallback((newDate: DateRange | undefined) => {
        setDate(newDate);

        if (newDate?.from || newDate?.to) {
            const startDate = newDate.from ? format(newDate.from, 'yyyy-MM-dd') : undefined;
            const endDate = newDate.to ? format(newDate.to, 'yyyy-MM-dd') : undefined;
            onDateChange(startDate, endDate, showDateTypeSelector ? dateType : undefined);
        } else {
            onDateChange(undefined, undefined, showDateTypeSelector ? dateType : undefined);
        }
    }, [onDateChange, dateType, showDateTypeSelector]);

    const handleDateTypeChange = React.useCallback((value: string) => {
        const newDateType = value as DateFilterType;
        setDateType(newDateType);

        if (date?.from || date?.to) {
            const startDate = date.from ? format(date.from, 'yyyy-MM-dd') : undefined;
            const endDate = date.to ? format(date.to, 'yyyy-MM-dd') : undefined;
            onDateChange(startDate, endDate, newDateType);
        }
    }, [date, onDateChange]);

    const handleClear = React.useCallback(() => {
        setDate(undefined);
        setDateType(DateFilterType.EMAIL_SENT_AT);
        onDateChange(undefined, undefined, showDateTypeSelector ? DateFilterType.EMAIL_SENT_AT : undefined);
        setOpen(false);
    }, [onDateChange, showDateTypeSelector]);

    const handleApply = React.useCallback(() => {
        setOpen(false);
    }, []);

    React.useImperativeHandle(ref, () => ({
        clear: handleClear
    }), [handleClear]);

    const displayText = React.useMemo(() => {
        if (date?.from) {
            if (date.to) {
                return `${format(date.from, 'dd/MM/yyyy', { locale: ptBR })} - ${format(date.to, 'dd/MM/yyyy', { locale: ptBR })}`;
            }
            return format(date.from, 'dd/MM/yyyy', { locale: ptBR });
        }
        return placeholder;
    }, [date, placeholder]);

    const hasValue = date?.from !== undefined;

    return (
        <div className={cn('grid gap-2', className)}>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        id="date"
                        variant={'outline'}
                        className={cn(
                            'h-8 min-h-[32px] w-auto justify-start text-left font-normal text-sm',
                            !date && 'text-muted-foreground',
                            hasValue && 'border-yellow-500 bg-yellow-50'
                        )}
                    >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {displayText}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                    {showDateTypeSelector && (
                        <div className="border-b p-3 sm:p-4">
                            <Label className="text-xs font-medium text-gray-700 mb-2 block">Filtrar por:</Label>
                            <RadioGroup
                                value={dateType}
                                onValueChange={handleDateTypeChange}
                                className="flex flex-row gap-4"
                            >
                                {DATE_TYPE_OPTIONS.map((option) => (
                                    <div key={option.value} className="flex items-center space-x-2">
                                        <RadioGroupItem value={option.value} id={option.value} />
                                        <Label htmlFor={option.value} className="text-sm font-normal cursor-pointer whitespace-nowrap">
                                            {option.label}
                                        </Label>
                                    </div>
                                ))}
                            </RadioGroup>
                        </div>
                    )}
                    <Calendar
                        initialFocus
                        mode="range"
                        locale={ptBR}
                        defaultMonth={date?.from}
                        selected={date}
                        onSelect={handleDateSelect}
                        numberOfMonths={2}
                    />
                    <div className="border-t bg-white p-3 sm:p-4 mt-auto shrink-0">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                            <p className="text-xs text-gray-500 order-2 sm:order-1">Clique nas datas para selecionar</p>
                            <div className="flex gap-2 w-full sm:w-auto order-1 sm:order-2">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 px-3 text-xs flex-1 sm:flex-none"
                                    onClick={handleClear}
                                >
                                    Limpar
                                </Button>
                                <Button
                                    size="sm"
                                    className="h-7 px-3 text-xs flex-1 sm:flex-none"
                                    onClick={handleApply}
                                >
                                    Aplicar
                                </Button>
                            </div>
                        </div>
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
});

DateRangePicker.displayName = "DateRangePicker";
